/**
 * <RealizationsPage> — « Activités terminées », ce que le legacy appelle les réalisations (page).
 *
 * C'est la destination du « Voir plus » sous les activités faites (`RealizationsTabScreen.js`) :
 * la liste de tout ce que l'enfant a réalisé, la plus récente en tête, chargée par pages. Chaque
 * ligne dit quand l'activité a été faite et où elle en est.
 *
 * Aucune sélection ici — le legacy n'en a pas sur cet écran. Vide, la page le dit en mots.
 *
 * **L'élève lit la même page à sa voix** (`voice="student"`) : son titre est « Activités faites »,
 * et la liste vide le tutoie — les mots du legacy (`TEXTS.student`). Toucher une ligne rouvre
 * alors l'activité terminée ; même callback, c'est l'écran qui sait où il mène. Ce qu'il voit en
 * plus se pose dans `topSlot` : la carte d'offre de l'élève sans abonnement (`BecomePremiumCard`),
 * qui dit la pause quand son abonnement est suspendu, et que le legacy ne montre qu'au-dessus d'une
 * liste — jamais sur la page vide.
 *
 * Avec `search`, la barre de recherche et ses puces tiennent la tête de la liste ; `activities`
 * arrive alors déjà filtrée par le serveur. Dès que l'élève a des activités faites, le chargement et
 * l'erreur restent sous la barre : la remplacer par le squelette ou la bannière de page ferait perdre
 * le clavier.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { ActivityRow } from "../../molecules/ActivityRow"
import { ActivityRowSkeleton } from "../../molecules/ActivityRow/ActivityRowSkeleton"
import { ActivitySearchBar } from "../../molecules/ActivitySearchBar"
import { CardList, CardListLinkRow } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { Header } from "../../molecules/Header"
import { RefreshButton } from "../../molecules/RefreshButton"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { type Voice } from "../../utils/voice"
import { isActivitySearchActive } from "../../../ports/primary/ui/formatters/activitySearchFormatter"
import { type UIActivity, type UIActivitySearch, type UISubject } from "../../../ports/primary/ui/types"
import { RealizationsSkeleton } from "./RealizationsSkeleton"

/** Les mots de chaque voix — ceux de `RealizationsTabScreen.js` (titres `:114-122`, `:155` ; `TEXTS`). */
const TEXTS: Record<Voice, { title: string; emptyTitle: string; emptyMessage: string }> = {
    parent: {
        title: "Activités terminées",
        emptyTitle: "Aucune activité réalisée pour l'instant",
        emptyMessage: "Les scores et les conseils personnalisés apparaîtront dès la première activité réalisée",
    },
    student: {
        title: "Activités faites",
        emptyTitle: "Tu n'as pas encore réalisé d'activité",
        emptyMessage:
            "Réalise ta première activité pour accéder à tes scores et des conseils personnalisés pour t'améliorer",
    },
}

export type RealizationsPageProps = {
    activities: UIActivity[]
    /** Toucher une activité : sa fiche pour le parent, l'activité terminée pour l'élève. */
    onOpenActivity?: (id: UIActivity["id"]) => void
    onClose?: () => void
    /** Un rafraîchissement est en cours : le bouton de l'en-tête tourne. */
    refreshing?: boolean
    onRefresh?: () => void
    /**
     * Au-dessus de la liste, quand il y en a une — la carte d'offre de l'élève sans abonnement.
     * Rien par défaut ; jamais montrée sur la page vide.
     */
    topSlot?: React.ReactNode
    /** À qui la page parle — `"parent"` par défaut. */
    voice?: Voice

    // ── Suite de la liste ──
    hasMore?: boolean
    loadingMore?: boolean
    onLoadMore?: () => void

    // ── Recherche ──
    search?: UIActivitySearch
    onSearchChange?: (next: UIActivitySearch) => void
    /**
     * Les matières des puces — celles des activités faites, que le serveur nomme filtres ignorés.
     * Non vide, il dit aussi que l'élève a des activités faites : la barre reste alors en place
     * quoi qu'il arrive (chargement, liste vide, erreur), car la démonter ferait perdre le clavier.
     */
    searchSubjects?: UISubject[]
    /** Le nombre d'activités qui passent la recherche, toutes pages comprises. */
    searchResultCount?: number

    // ── État de page ──
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function RealizationsPage(props: RealizationsPageProps) {
    const {
        activities,
        onOpenActivity,
        onClose,
        refreshing = false,
        onRefresh,
        topSlot,
        voice = "parent",
        hasMore = false,
        loadingMore = false,
        onLoadMore,
        search,
        onSearchChange,
        searchSubjects = [],
        searchResultCount,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    const texts = TEXTS[voice]
    const isEmpty = activities.length === 0
    const searching = search != null && isActivitySearchActive(search)
    const keepSearchBar = searching || searchSubjects.length > 0

    const header = (
        <Header
            title={texts.title}
            left={
                onClose != null ? (
                    <IconButton
                        iconName="times"
                        appearance="transparent"
                        iconColor="primary"
                        onPress={onClose}
                        accessibilityLabel="Fermer"
                    />
                ) : undefined
            }
            right={onRefresh != null ? <RefreshButton refreshing={refreshing} onRefresh={onRefresh} /> : undefined}
        />
    )

    const list = (
        <CardList>
            {activities.map((activity) => (
                <ActivityRow
                    key={activity.id}
                    activity={activity}
                    onPress={onOpenActivity != null ? () => onOpenActivity(activity.id) : undefined}
                />
            ))}
            {loadingMore ? (
                // La suite arrive : deux lignes de sa forme, sous un seul balayage.
                <DelayedSkeleton>
                    <ActivityRowSkeleton titleWidth={160} done />
                    <ActivityRowSkeleton titleWidth={120} done divided />
                </DelayedSkeleton>
            ) : hasMore && onLoadMore != null ? (
                <CardListLinkRow label="Voir plus" onPress={onLoadMore} />
            ) : null}
        </CardList>
    )

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            header={header}
            loading={loading && !keepSearchBar}
            skeleton={<RealizationsSkeleton />}
            error={keepSearchBar ? undefined : error}
            onRetry={onRetry}
            keyboardShouldPersistTaps="handled"
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            {search != null && onSearchChange != null && (!isEmpty || keepSearchBar) ? (
                <View style={styles.search}>
                    <ActivitySearchBar
                        value={search}
                        onChange={onSearchChange}
                        subjects={searchSubjects}
                        resultCount={searchResultCount ?? activities.length}
                    />
                </View>
            ) : null}
            {isEmpty && keepSearchBar && error != null && !loading ? (
                <EmptyState
                    title="La recherche n'a pas abouti"
                    message="Un nouvel essai avec la flèche en haut, ou un filtre en moins."
                />
            ) : isEmpty && keepSearchBar && (loading || !searching) ? (
                // Tout retiré, liste encore vide : l'élève a des activités faites, la liste complète
                // est en route.
                <DelayedSkeleton>
                    <ActivityRowSkeleton titleWidth={160} done />
                    <ActivityRowSkeleton titleWidth={120} done divided />
                </DelayedSkeleton>
            ) : isEmpty && searching ? (
                <EmptyState
                    title="Aucune activité trouvée"
                    message="Un autre mot, ou un filtre en moins : la liste complète revient dès que tout est retiré."
                />
            ) : isEmpty ? (
                <EmptyState title={texts.emptyTitle} message={texts.emptyMessage} />
            ) : topSlot != null ? (
                <Stack gap="6">
                    {topSlot}
                    {list}
                </Stack>
            ) : (
                list
            )}
        </PageTemplate>
    )
}

const styles = StyleSheet.create((theme) => ({
    search: { marginBottom: theme.spacing["4"] },
}))
